import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { motion, useScroll, useSpring } from "framer-motion";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";

// Landing page components
import { Header } from "./components/Header";
import { HeroSection } from "./components/sections/HeroSection";
import { ProblemSection } from "./components/sections/ProblemSection";
import { SolutionSection } from "./components/sections/SolutionSection";
import { NeedSelectorSection } from "./components/sections/NeedSelectorSection";
import { HowItWorksSection } from "./components/sections/HowItWorksSection";
import { TechnologySection } from "./components/sections/TechnologySection";
import { ComparisonSection } from "./components/sections/ComparisonSection";
import { SocialProofSection } from "./components/sections/SocialProofSection";
import { TrustControlSection } from "./components/sections/TrustControlSection";
import { BusinessPreviewSection } from "./components/sections/BusinessPreviewSection";
import { SecuritySection } from "./components/sections/SecuritySection";
import { OrganismesSection } from "./components/sections/OrganismesSection";
import { ProductMockupsSection } from "./components/sections/ProductMockupsSection";
import { CTASection } from "./components/sections/CTASection";
import { Footer } from "./components/sections/Footer";
import { DemoMode } from "./components/DemoMode";

// Auth pages
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { ForgotPasswordPage, ResetPasswordPage } from "./pages/PasswordResetPages";
import { AidQuizPage } from "./pages/AidQuizPage";
import { TaxPrepQuizPage } from "./pages/TaxPrepQuizPage";
import { BusinessPage } from "./pages/BusinessPage";

// Dashboard pages
import { DashboardPage } from "./pages/DashboardPage";
import { DocumentsPage } from "./pages/DocumentsPage";
import { AbonnementsPage } from "./pages/AbonnementsPage";
import { MDPPage } from "./pages/MDPPage";
import { AidesPage } from "./pages/AidesPage";
import { ImpotPage } from "./pages/ImpotPage";
import { OrganismePage } from "./pages/OrganismePage";
import { TasksPage } from "./pages/TasksPage";
import { FinancesPage } from "./pages/FinancesPage";
import { DocumentationPage } from "./pages/DocumentationPage";
import { AdminPage } from "./pages/AdminPage";
import { LettresPage } from "./pages/LettresPage";
import { RappelsPage } from "./pages/RappelsPage";
import { GamificationPage } from "./pages/GamificationPage";
import { BillingPage } from "./pages/BillingPage";
import { ConfigurationPage } from "./pages/ConfigurationPage";
import { TaxAssistantPage } from "./pages/TaxAssistantPage";
import { ProfilPage } from "./pages/ProfilPage";
import { DemarchesPage } from "./pages/DemarchesPage";
import { DossierPage } from "./pages/DossierPage";
import { RecapPage } from "./pages/RecapPage";
import { MentionsLegalesPage, ConfidentialitePage, ContactPage } from "./pages/LegalPages";

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { isDark } = useTheme();
  
  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0F0F1A]' : 'bg-[#FAFBFC]'}`}>
        <div className="w-8 h-8 border-2 border-[#81d8d0] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Landing Page
const LandingPage = () => {
  const { isDark } = useTheme();
  
  const scrollToCTA = () => {
    const ctaSection = document.getElementById('cta');
    if (ctaSection) {
      ctaSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#0F0F1A]' : 'bg-[#F8F7F4]'}`} data-testid="landing-page">
      {/* Scroll Progress Indicator */}
      <ScrollProgressBar />
      
      {/* Demo Mode — internal only, enabled via ?demo=1 in URL */}
      {new URLSearchParams(window.location.search).has('demo') && <DemoMode />}
      
        <Header onCtaClick={scrollToCTA} />
      
      <main>
        <HeroSection onCtaClick={scrollToCTA} />
        <NeedSelectorSection />
        <ProblemSection />
        <SolutionSection />
        <ProductMockupsSection />
        <OrganismesSection />
        <HowItWorksSection />
        <SecuritySection />
        <TechnologySection />
        <ComparisonSection />
        <BusinessPreviewSection />
        <SocialProofSection />
        <TrustControlSection />
        <CTASection />
      </main>
      
      <Footer />
    </div>
  );
};

// Scroll Progress Bar component
const ScrollProgressBar = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });
  
  return (
    <motion.div
      className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#81d8d0] to-[#4ECDC4] origin-left z-50"
      style={{ scaleX }}
    />
  );
};

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route 
        path="/login" 
        element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} 
      />
      <Route 
        path="/register" 
        element={user ? <Navigate to="/dashboard" replace /> : <RegisterPage />} 
      />
      <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
      <Route path="/reinitialiser-mot-de-passe" element={<ResetPasswordPage />} />
      <Route path="/quiz-aides" element={<AidQuizPage />} />
      <Route path="/quiz-impots" element={<TaxPrepQuizPage />} />
      <Route path="/business" element={<BusinessPage />} />
      
      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/documents"
        element={
          <ProtectedRoute>
            <DocumentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/abonnements"
        element={
          <ProtectedRoute>
            <AbonnementsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/mdp"
        element={
          <ProtectedRoute>
            <MDPPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/aides"
        element={
          <ProtectedRoute>
            <AidesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/impot"
        element={
          <ProtectedRoute>
            <ImpotPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/organisme"
        element={
          <ProtectedRoute>
            <OrganismePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/profil"
        element={
          <ProtectedRoute>
            <ProfilPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/demarches"
        element={
          <ProtectedRoute>
            <DemarchesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/demarches/:id"
        element={
          <ProtectedRoute>
            <DossierPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/tasks"
        element={
          <ProtectedRoute>
            <TasksPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/finances"
        element={
          <ProtectedRoute>
            <FinancesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/documentation"
        element={
          <ProtectedRoute>
            <DocumentationPage />
          </ProtectedRoute>
        }
      />
      
      {/* Admin route - hidden URL */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      
      {/* Lettres Magiques */}
      <Route
        path="/dashboard/lettres"
        element={
          <ProtectedRoute>
            <LettresPage />
          </ProtectedRoute>
        }
      />
      
      {/* Rappels */}
      <Route
        path="/dashboard/rappels"
        element={
          <ProtectedRoute>
            <RappelsPage />
          </ProtectedRoute>
        }
      />

      {/* Gamification */}
      <Route
        path="/dashboard/recompenses"
        element={
          <ProtectedRoute>
            <GamificationPage />
          </ProtectedRoute>
        }
      />

      {/* Billing */}
      <Route
        path="/dashboard/abonnement"
        element={
          <ProtectedRoute>
            <BillingPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/configuration"
        element={
          <ProtectedRoute>
            <ConfigurationPage />
          </ProtectedRoute>
        }
      />

      {/* Tax Assistant */}
      <Route
        path="/dashboard/assistant-fiscal"
        element={
          <ProtectedRoute>
            <TaxAssistantPage />
          </ProtectedRoute>
        }
      />

      {/* État du projet (public) */}
      <Route path="/etat-du-projet" element={<RecapPage />} />

      {/* Legal */}
      <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
      <Route path="/confidentialite" element={<ConfidentialitePage />} />
      <Route path="/contact" element={<ContactPage />} />
      
      {/* Catch all */}
            <Route
        path="/dashboard/wizard"
        element={<Navigate to="/dashboard/assistant-fiscal" replace />}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AppRoutes />
            <Toaster position="top-center" richColors />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
